import React from "react";
import styled from "styled-components";
import { Checkbox, Image, Link, Typography } from "@arco-design/web-react";
import { IconFile } from "@arco-design/web-react/icon";
import moment from "moment";

import { fileDimensions, formatBytes, formatDimensions, isImageFile, previewUrl, thumbUrl } from "./fileMeta";
import FileTagPicker from "./FileTagPicker";
import FileUsageLinks from "./FileUsageLinks";

const { Text } = Typography;

const Card = styled.div`
  border: 1px solid ${(props) => (props.$selected ? "rgb(var(--arcoblue-6))" : "var(--color-border-2)")};
  border-radius: 4px;
  background: var(--color-bg-2);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px 0 12px;
`;

// Fixed height so a row of cards lines up whatever shape the images are; the
// image itself is contained inside it rather than cropped, because knowing what
// an asset actually looks like is the point of this view.
const Thumb = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 180px;
  margin: 8px 12px;
  background: var(--color-fill-2);
  border-radius: 2px;
  overflow: hidden;
  color: var(--color-text-3);

  /* The style prop sizes Arco's wrapper, not the picture inside it, so a
     large image spilled out and showed only its top-left corner. */
  .arco-image,
  .arco-image-img {
    max-width: 100%;
    max-height: 180px;
    object-fit: contain;
  }
`;

const CardBody = styled.div`
  padding: 0 12px 12px 12px;
`;

const FileName = styled.div`
  font-weight: 600;
  color: var(--color-text-1);
  word-break: break-all;
  margin-bottom: 4px;
`;

const MetaLine = styled.div`
  font-size: 12px;
  color: var(--color-text-3);
`;

const TagRow = styled.div`
  margin-top: 8px;
`;

export default function FileCard({ file, selected, onToggleSelect, dimensions, onDimensions, allTags, onToggleTag, onNewTag, onDownloadFull, onShowVersions, showUsages, usages }) {
  const url = thumbUrl(file);
  const dimensionText = formatDimensions(fileDimensions(file, dimensions));

  // Without a thumbnail the full image is shown, so its natural size is
  // free here. A thumbnail's isn't the image's: the server sends that.
  const handleLoad = (e) => {
    const img = e && e.target;
    if (!img || !img.naturalWidth || !onDimensions || file.thumb_url) return;
    onDimensions(file.name, { width: img.naturalWidth, height: img.naturalHeight });
  };

  return (
    <Card $selected={selected}>
      <CardHeader>
        <Checkbox checked={selected} onChange={() => onToggleSelect(file.name)} />
      </CardHeader>

      <Thumb>
        {url ? (
          <Image
            src={url}
            alt={file.name}
            onLoad={handleLoad}
            previewProps={{ src: previewUrl(file) }}
            style={{ maxHeight: 180, maxWidth: "100%", objectFit: "contain" }}
            error={<IconFile style={{ fontSize: 32 }} />}
          />
        ) : (
          <IconFile style={{ fontSize: 32 }} />
        )}
      </Thumb>

      <CardBody>
        <FileName title={file.name}>{file.name}</FileName>
        <MetaLine>
          Size: {formatBytes(file.size)}
          {dimensionText && <> &nbsp;&nbsp; Dimensions: {dimensionText}</>}
        </MetaLine>
        {file.full_size > 0 && (
          <MetaLine>
            Full size kept: {formatBytes(file.full_size)}
            {onDownloadFull && (
              <> &nbsp;<Link style={{ fontSize: 12, padding: 0 }} onClick={() => onDownloadFull(file.name)}>Download full size</Link></>
            )}
          </MetaLine>
        )}
        {onShowVersions && isImageFile(file) && (
          <MetaLine>
            <Link style={{ fontSize: 12, padding: 0 }} onClick={() => onShowVersions(file)}>See all versions</Link>
          </MetaLine>
        )}
        {file.updated_date && (
          <MetaLine>
            <Text type="secondary" title={moment(file.updated_date).format("MMM D, YYYY h:mm A")}>
              Updated {moment(file.updated_date).fromNow()}
            </Text>
          </MetaLine>
        )}

        {showUsages && <FileUsageLinks usages={usages} />}

        <TagRow>
          <FileTagPicker
            file={file}
            allTags={allTags}
            onToggle={onToggleTag}
            onNewTag={onNewTag}
            size="small"
          />
        </TagRow>
      </CardBody>
    </Card>
  );
}
